import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ShieldCheck, Lock, KeyRound, Mail, Eye, AlertTriangle, FileCheck } from "lucide-react";
import { API } from "../config/api";

// Public-facing security overview. Linked from the footer. Goal: convert
// security-conscious creators (the ones who care about who they trust
// with their identity) into Producer subscribers on first visit.
//
// Tone: confident and specific. Every claim must be true today and
// verifiable in the codebase.
const ALLOWED_SOURCES = new Set(["footer", "pricing-badge", "direct"]);

const SecurityPage = () => {
  const { search } = useLocation();

  // Fire-and-forget analytics: tell the backend a /security view happened
  // and where it came from. Best-effort — failures are swallowed because
  // a broken analytics call must NEVER block the page render.
  useEffect(() => {
    const params = new URLSearchParams(search);
    const rawSrc = (params.get("src") || "").toLowerCase();
    const source = ALLOWED_SOURCES.has(rawSrc) ? rawSrc : "direct";
    axios.post(`${API}/events/security-page-view`, {
      source,
      referrer: document.referrer || null,
    }).catch(() => {});
  }, [search]);

  return (
  <div className="min-h-screen bg-black text-white" data-testid="security-page">
    <div className="max-w-3xl mx-auto px-4 py-12 sm:py-16">
      <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-white text-sm mb-10" data-testid="security-back-home">
        <ArrowLeft className="h-4 w-4" /> Back home
      </Link>

      <div className="flex items-center gap-3 mb-3">
        <div className="h-11 w-11 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
          <ShieldCheck className="h-6 w-6 text-emerald-400" />
        </div>
        <p className="text-xs uppercase tracking-widest text-emerald-400">Security</p>
      </div>
      <h1 className="text-4xl sm:text-5xl font-semibold leading-tight mb-4">
        Your account is the product. We protect it like one.
      </h1>
      <p className="text-gray-400 text-base sm:text-lg leading-relaxed mb-12 max-w-2xl">
        GlobalEyes.TV is built around <span className="text-white">anonymous channels</span>,
        and that promise only holds if the credentials behind those channels are airtight.
        Here's exactly how we guard them — no marketing fluff, just the stack.
      </p>

      <div className="grid gap-6 sm:gap-7">
        <Pillar
          icon={KeyRound}
          title="Strong passwords are mandatory at every entry point"
          body={
            <>
              Registration, password change, and password reset all enforce the same floor:
              <ul className="mt-2 space-y-1 text-sm text-gray-300 list-disc list-inside">
                <li>14 characters minimum</li>
                <li>At least one lowercase, uppercase, number, and symbol</li>
                <li>One-click "Suggest strong password" generator on every field, using your browser's cryptographic RNG</li>
              </ul>
              We don't blame users for picking <code className="text-amber-300 bg-amber-500/10 px-1.5 py-0.5 rounded text-xs">password</code> —
              we just make it impossible.
            </>
          }
        />

        <Pillar
          icon={AlertTriangle}
          title="Every new password is screened against 800M+ public breaches"
          body={
            <>
              Even a syntactically-strong password is worthless if it's already in a stolen-credential dump.
              Before we accept any new password, we screen it against <a href="https://haveibeenpwned.com/Passwords" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2">Have I Been Pwned</a>'s
              corpus of breached passwords using its <span className="text-white">k-anonymity protocol</span>:
              <ul className="mt-2 space-y-1 text-sm text-gray-300 list-disc list-inside">
                <li>Your password is SHA-1 hashed locally on our server</li>
                <li>Only the first 5 hex characters of that hash are sent to HIBP</li>
                <li>The match happens client-side against the returned suffix list</li>
              </ul>
              HIBP literally cannot tell which password we checked. Your plaintext never leaves our backend, ever.
            </>
          }
        />

        <Pillar
          icon={Lock}
          title="Passwords are hashed with bcrypt before storage"
          body={
            <>
              We never store your password — not in plaintext, not in reversible form. The only thing
              touching our database is a one-way bcrypt hash with a per-user salt. Even a complete
              database dump can't be used to log in as you.
            </>
          }
        />

        <Pillar
          icon={Mail}
          title="Real-time alerts when your password is changed"
          body={
            <>
              The moment your password is updated — from your account settings or via the email reset
              flow — we send you a confirmation email recording <span className="text-white">when, from what IP, and from what device</span>.
              If it wasn't you, a red one-click button locks the attacker out immediately.
            </>
          }
        />

        <Pillar
          icon={FileCheck}
          title="Single-use, hashed, expiring reset tokens"
          body={
            <>
              Email magic-links carry a 256-bit random token that expires 15 minutes after issue and can only be used once.
              We store the <span className="text-white">SHA-256 hash</span> of the token, not the raw value — so even if our
              database were dumped, the dump alone can't be used to reset anyone's password.
            </>
          }
        />

        <Pillar
          icon={Eye}
          title="Anti-enumeration on every auth response"
          body={
            <>
              Our "forgot password" endpoint returns the same generic message whether the email is
              registered or not. Attackers cannot use it to confirm whether someone has an account here.
              All login failures use identical response shapes regardless of whether the email or
              password was the wrong field — no information leaks through error patterns.
            </>
          }
        />

        <Pillar
          icon={ShieldCheck}
          title="Channel anonymity is enforced at the API layer"
          body={
            <>
              Every public response — videos, comments, follows, marketplace listings — exposes only
              the <span className="text-white">channel handle</span>. The owning master account ID and master
              username are never serialized into JSON that leaves this server. The channels feature is
              not just a UI label; it's a hard data boundary in the backend.
            </>
          }
        />
      </div>

      <div className="mt-14 p-6 bg-gray-900/60 border border-gray-800 rounded-2xl" data-testid="security-disclosure-box">
        <h2 className="text-lg font-semibold text-white mb-2">Found something we should fix?</h2>
        <p className="text-gray-400 text-sm leading-relaxed">
          Email <a href="mailto:security@globaleyes.tv" className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2">security@globaleyes.tv</a> with
          a clear reproduction. We acknowledge within 48 hours and credit reporters publicly (with permission) when issues are fixed.
        </p>
      </div>

      <p className="text-gray-600 text-xs mt-10 max-w-2xl">
        This page is updated whenever the security stack changes. Last updated May 12, 2026 ·
        GlobalEyes.TV is operated by NETBINGE, LLC.
      </p>
    </div>
  </div>
  );
};

const Pillar = ({ icon: Icon, title, body }) => (
  <div className="flex gap-4 p-5 sm:p-6 bg-gray-900/40 border border-gray-800 rounded-xl hover:border-gray-700 transition-colors">
    <div className="h-10 w-10 shrink-0 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mt-0.5">
      <Icon className="h-5 w-5 text-emerald-400" />
    </div>
    <div>
      <h3 className="text-white font-semibold text-base sm:text-lg mb-1.5">{title}</h3>
      <div className="text-gray-400 text-sm leading-relaxed">{body}</div>
    </div>
  </div>
);

export default SecurityPage;
